import { IGroup } from "@app/main/components/resourceManagement/groups/_models/IGroup";
import { DateService } from "@app/services/date.service";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { DateRange } from "@enums/DateRange.enum";
import { NotificationRecipientType } from "@enums/NotificationRecipientType";
import { NotificationRecordStatus } from "@enums/NotificationRecordStatus";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import * as angular from "angular";
import * as moment from "moment";
import { NotificationStatus } from "src/app/main/modules/alerts/_enums/alerts.enums";
import { IAlertRule, IAlertRuleRecord, INotification, INotificationRecord } from "../../_models/alerts.interfaces";
import { AlertsService } from "../../_services/alerts.service";
import './alertHistory.component.scss';

interface IComponentBindings {
    alertRule: IAlertRule;
}

class AlertHistoryComponentController implements angular.IComponentController, IComponentBindings {

    alertRule: IAlertRule;

    // raw data
    groups: IGroup[] = [];
    users: IUser[] = [];
    vehicles: IVehicle[] = [];

    selectedAlertRuleRecord: IAlertRuleRecord;
    alertRecords: IAlertRuleRecord[] = [];

    lastAlertNotification: INotification;
    alertNotifications: INotification[];

    // dataview
    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IAlertRuleRecord>[] = [];

    recipientTableHeaders: IVeriTableHeader[] = [];
    recipientTableRows: IVeriTableRow<INotificationRecord>[] = [];

    historyTableHeaders: IVeriTableHeader[] = [];
    historyTableRows: IVeriTableRow<INotification>[] = [];

    loadingAlertRecords = false;

    defaultDateRange: DateRange = DateRange.Latest7Days;
    dateFilter: IDateFilter;

    static $inject = ["$q", "alertsService", "dateService", "vehicleService", "veriTableService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly alertsService: AlertsService,
        private readonly dateService: DateService,
        private readonly vehicleService: VehicleService,
        private readonly veriTableService: VeriTableService,
        private readonly translationService: TranslationService    ) {
    }

    $onInit() {
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {

        this.dateFilter = this.dateService.dateRangeFromEnum(this.defaultDateRange);

        this.createTableHeaders();
        this.loadAlertRecords();
    }

    selectAlertRecord(alertRuleRecord: IAlertRuleRecord) {
        this.selectedAlertRuleRecord = alertRuleRecord;

        this.alertsService.getAlertNotifications(this.selectedAlertRuleRecord.RuleId, this.selectedAlertRuleRecord.Id)
            .then(notifications => {
                this.alertNotifications = notifications;
                this.lastAlertNotification = notifications[notifications.length - 1];
                this.fixNotificationRichHtmlLink(this.lastAlertNotification);

                this.getNotificationRecords(this.lastAlertNotification);
            });
    }

    clearSelectedAlertRecord() {
        this.selectedAlertRuleRecord = null;
    }


    getNotificationRecords(notification: INotification): void {
        this.alertsService.getAlertNotificationRecords(notification?.Id).then(notificationRecords => {
            notification.NotificationRecords = [...notificationRecords];

            this.createHistoryTable();
            this.createRecipientTable();
        });
    }

    onDateFilterChanged(filter: IDateFilter): void {
        this.dateFilter = filter;

        this.loadAlertRecords();
    }

    editRule(ruleRecord: IAlertRuleRecord, $event: MouseEvent) {
        this.alertsService.showAlertEditor(ruleRecord.rule, $event);
    }

    //-----

    private fixNotificationRichHtmlLink(notification: INotification): string {
        if (!notification?.Rich) {
            return;
        }

        notification.Rich = notification.Rich.replace('href="https://www.google.co.uk/maps/search', 'target="_blank" href="https://www.google.co.uk/maps/search');
        notification.Rich = notification.Rich.replace('href="http://www.verilocation.com', 'target="_blank" href="http://www.verilocation.com');
    }

    private loadAlertRecords() {
        this.loadingAlertRecords = true;

        const rulePromise = this.alertsService.getActiveAlertRules();
        const vehiclePromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const recordsPromise = this.alertRule
            ? this.alertsService.getAlertRuleRecords(this.alertRule.Id, moment(this.dateFilter.start), moment(this.dateFilter.end))
            : this.alertsService.getAlertRuleRecordsForAccount(moment(this.dateFilter.start), moment(this.dateFilter.end));

        this.$q.all([rulePromise, vehiclePromise, recordsPromise])
            .then(([rules, vehiclePromise, records]) => {
                this.alertRecords = records;

                this.populateRecordsWithMetaData(records, rules, vehiclePromise);

                this.createTableRows();
            })
            .finally(() => {
                this.loadingAlertRecords = false;
            });
    }

    private populateRecordsWithMetaData(records: IAlertRuleRecord[], rules: IAlertRule[], vehiclePromise: IVehicle[]) {
        if (!records) {
            return;
        }

        records.forEach(record => {
            record.rule = rules.find(r => r.Id == record.RuleId);
            record.vehicle = vehiclePromise.find(v => v.Id == record.VehicleId);

            if (record?.vehicle?.TagIds) {
                record.tagIds = [];

                record.tagIds = record.tagIds.concat(record.vehicle.TagIds);
            }
        });
    }

    private createTableHeaders() {
        this.tableHeaders = [
            this.veriTableService.createDateTimeHeader(this.translationService.t("AlertHistoryComponent.lastSent")),
            this.veriTableService.createVehicleRegHeader(),
            { title: this.translationService.t("AlertHistoryComponent.alertRule"), width: 200, sortable: true, hide: this.alertRule != null },
            { title: this.translationService.t("AlertHistoryComponent.Description"), sortable: true, hideOnMobile: true },
        ];
    }

    private createTableRows() {
        this.tableRows = [];

        if (!this.alertRecords) {
            return;
        }

        // Hide all records without a rule
        // Hide all sent count 0, this just means raised but not sent (i.e. delay not been hit)
        const rulesToShow = this.alertRecords.filter(r => r.rule && r.SentCount > 0);

        this.tableRows = this.veriTableService.createTableRowsWithCells(rulesToShow, record => this.createTableRowCells(record));
    }

    private createTableRowCells(record: IAlertRuleRecord): IVeriTableRowCell[] {
        return [
            this.veriTableService.createDateTimeCell(record.LastSent),
            this.veriTableService.createVehicleRegCell(record.vehicle),
            { contents: record.rule?.Name },
            { contents: record.Subject }
        ];
    }

    private createHistoryTable() {
        this.historyTableHeaders = [
            { title: this.translationService.t("AlertHistoryComponent.status"), sortable: true, width: 80 },
            this.veriTableService.createDateTimeHeader(),
            { title: this.translationService.t("AlertHistoryComponent.recipients"), sortable: true },
        ];

        let recipients = this.alertNotifications?.filter(n => n.NotificationRecords.length > 0)[0]?.NotificationRecords?.map(r => r.Recipient)?.join(", ");

        this.historyTableRows = this.veriTableService.createTableRowsWithCells(this.alertNotifications, notification => {
            var cells: IVeriTableRowCell[] = [];

            cells.push({ contents: NotificationStatus[notification.NotificationStatus] });
            cells.push(this.veriTableService.createDateTimeCell(notification.Processed));
            cells.push({ contents: recipients });

            return cells;
        });
    }

    private createRecipientTable() {
        this.recipientTableHeaders = [
            { title: this.translationService.t("AlertHistoryComponent.status"), sortable: true, width: 80 },
            this.veriTableService.createDateTimeHeader(),
            { title: this.translationService.t("AlertHistoryComponent.method"), sortable: true, width: 100 },
            { title: this.translationService.t("AlertHistoryComponent.recipient"), sortable: true },
        ];

        this.recipientTableRows = [];

        if (!this.lastAlertNotification) {
            return;
        }

        this.recipientTableRows = this.veriTableService.createTableRowsWithCells(this.lastAlertNotification.NotificationRecords, record => {
            var cells: IVeriTableRowCell[] = [];

            cells.push({ contents: NotificationRecordStatus[record.NotificationStatus] });
            cells.push(this.veriTableService.createDateTimeCell(record.TimestampSent));
            cells.push({ contents: NotificationRecipientType[record.RecipientType] });
            cells.push({ contents: record.Recipient });

            return cells;
        });
    }
}

export class AlertHistoryComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            alertRule: "<",
        };

        this.controller = AlertHistoryComponentController;
        this.template = require("./alertHistory.component.html");
    }
}
